/** Blocs VAD (carte intégrée, catalogue agrégé) et carrousel immersif. */
import React from "react";
import { toast } from "sonner";
import { ShoppingBag, ZoomIn, X } from "lucide-react";
import { api } from "../../lib/api";
import { useCart } from "../../contexts/CartContext";
import { useI18n } from "../../lib/i18n";

// ─── Bloc "Carrousel immersif" ─────────────────────────────────────────────
export function CarouselBlock({ d = {} }) {
  const images = (d.images || []).filter(Boolean);
  const [idx, setIdx] = React.useState(0);
  const timer = React.useRef(null);
  const { t } = useI18n();

  React.useEffect(() => {
    if (d.autoplay === false || images.length < 2) return;
    timer.current = setInterval(() => setIdx((i) => (i + 1) % images.length), 4500);
    return () => clearInterval(timer.current);
  }, [images.length, d.autoplay]);

  if (images.length === 0) return null;
  const go = (n) => {
    clearInterval(timer.current);
    setIdx((n + images.length) % images.length);
  };

  return (
    <div className="mb-16" data-testid="carousel-block">
      {(d.eyebrow || d.title) && (
        <div className="mb-6">
          {d.eyebrow && <p className="text-[11px] uppercase tracking-[0.3em] text-or mb-1">{t(d.eyebrow)}</p>}
          {d.title && <h2 className="font-serif text-3xl md:text-4xl text-domaine">{t(d.title)}</h2>}
          {d.subtitle && <p className="mt-2 text-ardoise/70">{t(d.subtitle)}</p>}
        </div>
      )}
      <div className="relative overflow-hidden group aspect-[16/9] md:aspect-[21/9] bg-domaine">
        {images.map((img, i) => (
          <img key={img + i} src={img.startsWith("http") ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`}
               alt={d.title || `photo ${i + 1}`} loading={i === 0 ? "eager" : "lazy"}
               className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`} />
        ))}
        <button onClick={() => go(idx - 1)} aria-label="Précédent"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 text-lin backdrop-blur-sm hover:bg-black/60 transition-colors opacity-0 group-hover:opacity-100 flex items-center justify-center"
                data-testid="carousel-prev">‹</button>
        <button onClick={() => go(idx + 1)} aria-label="Suivant"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 text-lin backdrop-blur-sm hover:bg-black/60 transition-colors opacity-0 group-hover:opacity-100 flex items-center justify-center"
                data-testid="carousel-next">›</button>
        <div className="absolute bottom-4 inset-x-0 flex justify-center gap-2">
          {images.map((_, i) => (
            <button key={i} onClick={() => go(i)} aria-label={`Photo ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-or" : "w-3 bg-lin/60 hover:bg-lin"}`} />
          ))}
        </div>
        <span className="absolute top-4 right-4 px-2.5 py-1 text-[11px] bg-black/50 text-lin backdrop-blur-sm">
          {idx + 1} / {images.length}
        </span>
      </div>
    </div>
  );
}

// ─── Bloc "Carte VAD intégrée" ─────────────────────────────────────────────
export function VadCardEmbedBlock({ qrToken }) {
  const [state, setState] = React.useState({ loading: true, card: null, products: [], available: false });

  React.useEffect(() => {
    if (!qrToken) { setState({ loading: false, card: null, products: [], available: false }); return; }
    let cancelled = false;
    api.get(`/vad/cards/${qrToken}`)
      .then(({ data }) => !cancelled && setState({ loading: false, ...data }))
      .catch(() => !cancelled && setState({ loading: false, card: null, products: [], available: false }));
    return () => { cancelled = true; };
  }, [qrToken]);

  if (!qrToken) {
    return (
      <div className="border border-dashed border-pierre p-8 text-center text-ardoise/60 text-sm">
        Sélectionne une carte VAD via l'éditeur CMS pour l'afficher ici.
      </div>
    );
  }
  if (state.loading) return <div className="text-center py-8 text-ardoise/50">Chargement de la carte…</div>;
  if (!state.card) {
    return <div className="text-center py-8 text-ardoise/60">Carte VAD introuvable.</div>;
  }

  const orderUrl = `/carte/${qrToken}`;

  return (
    <div data-testid="vad-card-embed" className="space-y-6">
      {!state.available && (
        <div className="border border-or/40 bg-or/5 p-4 text-sm text-ardoise/70">
          Cette carte n'est pas disponible actuellement.
        </div>
      )}
      {state.available && state.products.length > 0 && (
        <div className="flex flex-col divide-y divide-pierre border border-pierre">
          {state.products.map((p) => (
            <div key={p.id} className="flex items-center gap-4 p-4 bg-lin hover:bg-sable/20 transition-colors">
              <div className="w-20 h-20 flex-shrink-0 bg-sable/40 border border-pierre overflow-hidden">
                {p.photo_url ? (
                  <img src={p.photo_url.startsWith("http") ? p.photo_url : `${process.env.REACT_APP_BACKEND_URL}${p.photo_url}`}
                       alt={p.name} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] text-ardoise/40 uppercase tracking-widest">Photo</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-ardoise">{p.name}</h4>
                {p.description && (
                  <p className="text-xs text-ardoise/70 line-clamp-2 mt-1 max-w-xl">{p.description}</p>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-lg font-serif text-domaine">
                  {(p.unit_price_cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
                </div>
                {p.tracks_stock && p.stock_qty !== null && p.stock_qty !== undefined && (
                  <div className={`text-[11px] uppercase tracking-widest ${p.stock_qty > 0 ? "text-ardoise/60" : "text-red-600"}`}>
                    {p.stock_qty > 0 ? `Stock : ${p.stock_qty}` : "Épuisé"}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {state.available && (
        <div className="text-center pt-4">
          <a
            href={orderUrl}
            target="_blank" rel="noopener noreferrer"
            data-testid="vad-order-cta"
            className="inline-block px-8 py-3 text-[11px] uppercase tracking-widest bg-domaine text-lin hover:bg-or transition-colors"
          >
            Commander maintenant
          </a>
        </div>
      )}
    </div>
  );
}

// ─── Bloc "Catalogue VAD agrégé" ───────────────────────────────────────────
export function VadCatalogBlock() {
  const [data, setData] = React.useState({ families: [], cards: [] });
  const [loading, setLoading] = React.useState(true);
  const [activeFam, setActiveFam] = React.useState("all");
  const { locale } = useI18n();

  React.useEffect(() => {
    let cancelled = false;
    api.get(`/vad/catalog?locale=${encodeURIComponent(locale)}`)
      .then(({ data }) => !cancelled && setData(data))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [locale]);

  if (loading) return <div className="text-center py-8 text-ardoise/50">Chargement du catalogue…</div>;
  if (data.families.length === 0) {
    return (
      <div className="text-center py-16 border border-dashed border-pierre text-ardoise/60">
        Aucun produit publié pour le moment.<br />
        Coche « Publier sur le site web » sur au moins une carte VAD active.
      </div>
    );
  }

  const visible = activeFam === "all" ? data.families : data.families.filter((f) => f.slug === activeFam);
  const totalProducts = data.families.reduce((n, f) => n + f.products.length, 0);

  return (
    <div data-testid="vad-catalog" className="space-y-10">
      {data.families.length > 1 && (
        <div className="flex flex-wrap justify-center gap-2">
          <button
            onClick={() => setActiveFam("all")}
            className={`px-4 py-2 text-[11px] uppercase tracking-widest transition-colors ${
              activeFam === "all" ? "bg-domaine text-lin" : "border border-pierre text-ardoise hover:bg-domaine hover:text-lin"
            }`}
          >
            Tous ({totalProducts})
          </button>
          {data.families.map((f) => (
            <button key={f.slug}
              onClick={() => setActiveFam(f.slug)}
              className={`px-4 py-2 text-[11px] uppercase tracking-widest transition-colors ${
                activeFam === f.slug ? "bg-domaine text-lin" : "border border-pierre text-ardoise hover:bg-domaine hover:text-lin"
              }`}>
              {f.name} ({f.products.length})
            </button>
          ))}
        </div>
      )}

      {visible.map((f) => (
        <div key={f.slug}>
          <h3 className="text-xs uppercase tracking-[0.25em] text-domaine mb-4 pb-2 border-b border-pierre">
            {f.name}
          </h3>
          {f.description && <p className="text-sm text-ardoise/70 mb-4">{f.description}</p>}
          <div className="flex flex-col divide-y divide-pierre/60">
            {f.products.map((p, i) => (
              <VadCatalogRow key={`${p.id}-${i}`} product={p} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function VadCatalogRow({ product: p }) {
  const price = (p.unit_price_cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const { addItem, setDrawerOpen } = useCart();
  const outOfStock = p.tracks_stock && (p.stock_qty === 0);
  const [showFullDesc, setShowFullDesc] = React.useState(false);
  const [photoOpen, setPhotoOpen] = React.useState(false);
  const photoSrc = p.photo_url
    ? (p.photo_url.startsWith("http") ? p.photo_url : `${backend}${p.photo_url}`)
    : null;
  const hasLongDesc = (p.description || "").length > 140;
  const handleAdd = () => {
    if (outOfStock) return;
    addItem({
      product_id: p.id,
      card_id: p.card_id,
      card_qr_token: p.card_qr_token,
      card_name: p.card_name,
      name: p.name,
      description: p.description,
      photo_url: p.photo_url,
      unit_price_cents: p.unit_price_cents,
      tva_rate: p.tva_rate ?? 20,
      is_discountable: p.is_discountable !== false,
      tracks_stock: p.tracks_stock === true,
      stock_qty: p.stock_qty,
    }, 1);
    toast.success(`${p.name} ajouté au panier`);
    setDrawerOpen(true);
  };
  return (
    <div className="flex items-center gap-4 py-4">
      <button
        type="button"
        onClick={() => photoSrc && setPhotoOpen(true)}
        disabled={!photoSrc}
        data-testid={`catalog-photo-${p.id}`}
        className="group relative w-20 h-20 flex-shrink-0 bg-sable/40 border border-pierre overflow-hidden disabled:cursor-default cursor-zoom-in"
        aria-label={photoSrc ? `Agrandir la photo de ${p.name}` : undefined}
      >
        {photoSrc ? (
          <>
            <img src={photoSrc} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <ZoomIn className="w-5 h-5 text-white" />
            </span>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[10px] text-ardoise/40 uppercase tracking-widest">Photo</div>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-medium text-ardoise">{p.name}</h4>
          {p.tags && p.tags.map((t) => (
            <span key={t.id} title={t.description || t.name}
                  style={t.color ? { backgroundColor: t.color + "22", color: t.color } : {}}
                  className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-domaine/10 text-domaine flex items-center gap-1">
              {t.icon_type === "emoji" && t.icon_value && <span>{t.icon_value}</span>}
              {t.name}
            </span>
          ))}
        </div>
        {p.description && (
          <>
            <p className={`text-xs text-ardoise/70 mt-1 max-w-xl ${showFullDesc ? "" : "line-clamp-2"}`}>
              {p.description}
            </p>
            {hasLongDesc && (
              <button
                type="button"
                onClick={() => setShowFullDesc((v) => !v)}
                data-testid={`catalog-readmore-${p.id}`}
                className="text-[10px] uppercase tracking-widest text-domaine hover:underline mt-1"
              >
                {showFullDesc ? "Lire moins" : "Lire plus"}
              </button>
            )}
          </>
        )}
        {p.card_name && (
          <p className="text-[10px] uppercase tracking-widest text-ardoise/50 mt-1">Carte : {p.card_name}</p>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-lg font-serif text-domaine">{price}</div>
        {p.tracks_stock && p.stock_qty !== null && p.stock_qty !== undefined && (
          <div className={`text-[10px] uppercase tracking-widest ${p.stock_qty > 0 ? "text-ardoise/60" : "text-red-600"}`}>
            {p.stock_qty > 0 ? `Stock : ${p.stock_qty}` : "Épuisé"}
          </div>
        )}
      </div>
      <button
        onClick={handleAdd}
        disabled={outOfStock}
        data-testid={`catalog-add-${p.id}`}
        className="flex-shrink-0 inline-flex items-center gap-1 px-4 py-2 text-[10px] uppercase tracking-widest border border-domaine text-domaine hover:bg-domaine hover:text-lin transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-domaine"
      >
        <ShoppingBag size={12} />
        {outOfStock ? "Épuisé" : "Ajouter"}
      </button>

      {/* Lightbox photo */}
      {photoOpen && photoSrc && (
        <div
          onClick={() => setPhotoOpen(false)}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out"
          data-testid={`catalog-photo-modal-${p.id}`}
          role="dialog"
          aria-label={`Photo de ${p.name}`}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setPhotoOpen(false); }}
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2"
            aria-label="Fermer"
            data-testid={`catalog-photo-close-${p.id}`}
          >
            <X className="w-6 h-6" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-4xl max-h-[90vh] flex flex-col items-center">
            <img
              src={photoSrc}
              alt={p.name}
              className="max-w-full max-h-[80vh] object-contain shadow-2xl"
            />
            <p className="text-white/90 mt-4 text-lg font-serif">{p.name}</p>
          </div>
        </div>
      )}
    </div>
  );
}
